import { visibleCells, cellRect } from "@/lib/geometry";
import { subtypeOf, leafWeight } from "@/lib/iwin";
import { computeAssembly } from "@/lib/assembly";

const isSliding = (op) => op === "sliding_left" || op === "sliding_right";
const isSash = (op) => op.startsWith("casement") || op.startsWith("tilt") || op === "awning" || op === "hopper" || op.startsWith("pivot");
const isDoor = (op) => op.startsWith("door") || op.startsWith("folding");
const ROLLER = (kg) => (kg <= 80 ? ["single", 80] : kg <= 150 ? ["double", 150] : ["triple", 250]);
const FRAME_ITEMS = [["Corner cleats", "corner_cleat", "Aluminium cleat, crimped / pinned"], ["Shear blocks", "shear_block", "Mullion / transom joints"],
  ["Anchor fixings", "anchor", "Ø6 SS screw + plug, max 500 mm c/c, 150 mm from corners"], ["Perimeter silicone", "perimeter_seal", "Neutral-cure silicone + backer rod, both sides"]];

export const fmtQty = (h, mult = 1) => (h.unit === "m" ? `${(h.qty * mult).toFixed(1)} m` : `${h.qty * mult}`);

function slidingHardware(item, P, add) {
  const sl = P.filter((p) => isSliding(p.op));
  sl.forEach((p) => {
    const kg = leafWeight(item, p.r, p.c), [t, cap] = ROLLER(kg);
    const lockX = p.op === "sliding_right" ? p.x + 45 : p.x + p.w - 45, interX = p.op === "sliding_right" ? p.x + p.w - 40 : p.x + 40;
    const yAt = (fromSill) => Math.min(p.y + p.h - 80, Math.max(p.y + 80, item.height - fromSill));
    add(`roller_${t}`, `Rollers · ${t}`, `POM (YKK AP), ${cap} kg per shutter`, 2, `2 per shutter, leaf ~${Math.round(kg)} kg`, [[p.x + 120, p.y + p.h - 25], [p.x + p.w - 120, p.y + p.h - 25]]);
    add("anti_lift", "Anti-lift device", "ASA thermoplastic, top rail", 2, "2 per shutter", [[p.x + 120, p.y + 25], [p.x + p.w - 120, p.y + 25]]);
    add("hook_lock", "Hook lock", "SUS304, lock stile 800–1000 mm", 1, "1 per shutter", [[lockX, yAt(850)]]);
    add("handle_sl", "Flush handle", "SUS304, at 800–1000 mm", 1, "1 per shutter", [[lockX, yAt(1050)]]);
    add("end_cap", "End caps · stoppers", "Interlock end caps / shutter stopper", 2, "2 per shutter, interlock stile", [[interX, p.y + 70], [interX, p.y + p.h - 70]]);
  });
  const joints = [];
  P.forEach((a) => P.forEach((b) => {
    if (b.x > a.x && Math.abs(b.x - (a.x + a.w)) < 200 && Math.abs(b.y - a.y) < 1 && (isSliding(a.op) || isSliding(b.op))) {
      joints.push({ x: (a.x + a.w + b.x) / 2, y: a.y, h: Math.min(a.h, b.h), meeting: a.op === "sliding_left" && b.op === "sliding_right" });
    }
  }));
  joints.filter((j) => !j.meeting).forEach((j) => add("wind_stopper", "Wind stopper", "PA6 nylon, at interlocks", 2, "2 per interlock (top & bottom)", [[j.x, j.y + 170], [j.x, j.y + j.h - 170]]));
  if (sl.length) {
    const n = Math.max(2, Math.ceil(sl.reduce((a, p) => a + p.w, 0) / 1000));
    add("drain_valve", "Drain valve", "One-way sill drainage valve", n, "2 min, 1 per 1000 mm of sill", Array.from({ length: n }, (_, i) => [item.width * (i + 1) / (n + 1), item.height - 12]));
  }
  return sl;
}

function sashHardware(P, add) {
  const sash = P.filter((p) => isSash(p.op));
  sash.forEach((p) => {
    const top = p.op === "awning", hingeL = p.op.endsWith("left");
    const hx = hingeL ? p.x + 30 : p.x + p.w - 30, lx = hingeL ? p.x + p.w - 30 : p.x + 30;
    add("friction_stay", "Friction stays", "Stainless steel, opens to 90°", 2, "1 pair per sash", top ? [[p.x + 30, p.y + 80], [p.x + p.w - 30, p.y + 80]] : [[hx, p.y + 60], [hx, p.y + p.h - 60]]);
    add("multipoint_sash", "Multipoint lock + handle", "Multipoint only (casement / top hung)", 1, "1 set per sash", [top ? [p.x + p.w / 2, p.y + p.h - 40] : [lx, p.y + p.h / 2]]);
    add("child_lock", "Child lock", "Restricts opening to 110 mm", 1, "1 per sash", [top ? [p.x + p.w / 2 + 140, p.y + p.h - 40] : [lx, p.y + p.h / 2 + 160]]);
  });
  return sash;
}

function doorHardware(item, P, add) {
  const doors = P.filter((p) => isDoor(p.op));
  doors.forEach((p, i) => {
    const hingeL = p.op.endsWith("left"), hx = hingeL ? p.x + 25 : p.x + p.w - 25, lx = hingeL ? p.x + p.w - 45 : p.x + 45;
    const n = p.h > 2100 ? 4 : 3;
    add("hinge", "Door hinges", "Heavy-duty 3D adjustable", n, `${n} per leaf (${p.h > 2100 ? "> 2100" : "up to 2100"} mm)`, Array.from({ length: n }, (_, k) => [hx, p.y + 200 + (k * (p.h - 400)) / (n - 1)]));
    if (doors.length > 1 && i === 1) add("flush_bolt", "Flush bolts", "Top & bottom, passive leaf", 2, "2 per passive leaf", [[lx, p.y + 150], [lx, p.y + p.h - 150]]);
    else {
      add("multipoint_door", "Multipoint lock + latch", "IWIN-S door lock, latch option", 1, "1 per active leaf", [[lx, item.height - 1150]]);
      add("lever", "Lever handle set", "SUS304, at 1000–1050 mm", 1, "1 per active leaf", [[lx, item.height - 1000]]);
    }
  });
  return doors;
}

export function iwinAccessories(item) {
  const P = visibleCells(item).map((v) => ({ ...v, op: item.cells[v.r][v.c], ...cellRect(item, v.r, v.c) }));
  const rows = [];
  const add = (key, name, spec, qty, basis, pos = [], unit = "nos") => {
    if (qty <= 0) return;
    const row = rows.find((x) => x.key === key);
    if (row) { row.qty += qty; row.pos.push(...pos); } else rows.push({ key, name, spec, qty, basis, unit, pos: [...pos] });
  };
  const sl = slidingHardware(item, P, add), sash = sashHardware(P, add), doors = doorHardware(item, P, add);
  const asm = computeAssembly(item), W = item.width, H = item.height;
  const mt = asm.members.filter((m) => /Mullion|Transom/.test(m.name)).reduce((a, m) => a + m.qty, 0);
  add("sealer_pad", "Sealer pads", "IIR butyl, 90° joints (350 Pa)", 4 + 4 * (sl.length + doors.length) + 2 * mt, "4 frame corners + 4 per shutter/leaf + 2 per mullion/transom",
    [[30, 30], [W - 30, 30], [30, H - 30], [W - 30, H - 30]]);
  const glazing = parseFloat(asm.accessories.find((a) => a[0].startsWith("EPDM wedge"))?.[1]) || 0;
  add("epdm_glazing", "EPDM glazing gaskets", "AT material, inner & outer glass groove", glazing, "Glass perimeter × 2", [], "m");
  add("epdm_weather", "EPDM weather seals", "AT material, replaces wool pile", sl.concat(sash, doors).reduce((a, p) => a + 2 * (p.w + p.h), 0) / 1000, "Shutter / sash / leaf perimeter", [], "m");
  add("setting_block", "Setting blocks", "EPDM / PVC under each pane", 2 * asm.glass.reduce((a, g) => a + g.qty, 0), "2 per pane");
  if (item.railing?.height) {
    const bays = sl.filter((p) => p.r + (p.rs || 1) === item.rows.length);
    add("railing_rail", "Integrated railing rails", "Top & bottom rail, 18 mm glass groove", 2 * bays.length, `${(2 * bays.reduce((a, p) => a + p.w - 20, 0) / 1000).toFixed(2)} m total`);
    add("railing_glass", "Railing glass", `Toughened / laminated · H ${item.railing.height - (item.railing.bottom || 150) - 80} mm`, bays.length, "1 per railing bay");
  }
  FRAME_ITEMS.forEach(([label, key, spec]) => {
    const v = asm.accessories.find((a) => a[0].startsWith(label))?.[1];
    if (v) add(key, label, spec, parseFloat(v), "Frame assembly", [], v.endsWith(" m") ? "m" : "nos");
  });
  return rows.map((r, i) => ({ ...r, mark: `H${i + 1}` }));
}
